import React, { PropsWithChildren, ReactNode, useMemo } from 'react';
import { Stack, Typography } from '@mui/material';
import cx from 'classnames';
import { ScreenComponent } from 'new/routes/RouterManager';
import {
  MetricsDateControls,
  MetricsPresetKey,
} from '@modules/monitor/resource-triage/components/MetricsTrendPanel';
import { ResourceCount } from 'new/components/dashboard/ResourceCount';
import { KnowledgePanel } from 'new/elements/KnowledgePanel';
import {
  Header,
  SectionSelector,
  OrgPersonalToggle,
  AccountUsageMetrics,
  AccountDailyTrends,
  ResourceUsageChart,
} from './components';
import { ROUTE_REDESIGN } from 'js/constants/pathnames';
import { useFeatureFlag } from 'new/hooks/useFeatureFlags';
// Hooks
import { useAccountMetricsView } from './hooks/useAccountMetricsView';
import { useScrollToSection } from './hooks/useScrollToSection';

// Styles
import styles from './Utilization.module.scss';

// Utilization header omits the "1y" preset; offer 7d/30d/90d only.
const HEADER_DATE_PRESETS: MetricsPresetKey[] = ['7d', '30d', '90d'];

type SectionProps = {
  title?: string | ReactNode;
  titleAcessory?: ReactNode;
  rightView?: ReactNode;
  id?: string;
  className?: string;
};

export const Section = ({
  children,
  title,
  className,
  titleAcessory,
  id,
  rightView,
}: PropsWithChildren<SectionProps>) => {
  return (
    <Stack
      id={id}
      padding="16px"
      paddingLeft="0"
      paddingRight="0"
      className={cx(styles.section, className)}
    >
      {!!title ? (
        <Stack
          marginBottom="16px"
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" gap="16px">
            <Typography variant="subheading" component="div">
              {title}
            </Typography>
            {titleAcessory}
          </Stack>
          {rightView}
        </Stack>
      ) : null}
      {children}
    </Stack>
  );
};

export const Utilization: ScreenComponent<any> = () => {
  const showFlowActivity = useFeatureFlag('UI_ENABLE_UTILIZATION_FLOW_ACTIVITY');
  const {
    isAdmin,
    metrics,
    viewType,
    dates,
    daysDiff,
    isOrgView,
    onDatesChanged,
    onControlChange,
    isLoading,
  } = useAccountMetricsView();

  const { scrollToSection } = useScrollToSection();

  const sections = useMemo(
    () => [
      { id: 'accountUsage', label: 'Account Summary' },
      ...(showFlowActivity ? [{ id: 'flowActivity', label: 'Daily Usage' }] : []),
      { id: 'readOperations', label: 'Read Operations' },
      { id: 'writeOperations', label: 'Write Operations' },
      { id: 'resourceCount', label: 'Resource Count' },
    ],
    [showFlowActivity],
  );

  const orgPersonalToggle = useMemo(
    () => (isAdmin ? <OrgPersonalToggle checked={isOrgView} onChange={onControlChange} /> : null),
    [isAdmin, isOrgView, onControlChange],
  );

  const headerRight = useMemo(
    () => (
      <Stack direction="row" alignItems="center" gap="16px">
        <MetricsDateControls
          start={dates.startDate}
          end={dates.endDate}
          presets={HEADER_DATE_PRESETS}
          onDatesChanged={(next) => onDatesChanged(new Date(next.start), new Date(next.end))}
        />
        <SectionSelector sections={sections} onSectionSelect={scrollToSection} />
      </Stack>
    ),
    [dates.startDate, dates.endDate, onDatesChanged, scrollToSection, sections],
  );

  return (
    <Stack width="100%" position="relative">
      <Header childAsideOfTitle={orgPersonalToggle} childRightContent={headerRight} />

      <Stack padding="16px" paddingTop="0">
        <Section
          id="accountUsage"
          title={
            <KnowledgePanel
              alwaysShowUnderline
              knowledgeContent="Billing cycle starts at midnight UTC on first of each month."
            >
              Account Summary
            </KnowledgePanel>
          }
        >
          <AccountUsageMetrics
            daysDiff={daysDiff}
            metrics={metrics}
            viewType={viewType}
            isLoading={isLoading}
          />
        </Section>

        {showFlowActivity && (
          <Section id="flowActivity" title="Daily Usage">
            <div className={styles.sectionInnerContainer}>
              <AccountDailyTrends
                viewType={viewType}
                start={dates.startDate}
                end={dates.endDate}
                onDatesChanged={onDatesChanged}
              />
            </div>
          </Section>
        )}

        <Section id="readOperations" title="Read Operations">
          <div className={styles.sectionInnerContainer}>
            <ResourceUsageChart
              resource="source"
              title="Daily Read Trends"
              viewType={viewType}
              start={dates.startDate}
              end={dates.endDate}
              onDatesChanged={onDatesChanged}
            />
          </div>
        </Section>

        <Section id="writeOperations" title="Write Operations">
          <div className={styles.sectionInnerContainer}>
            <ResourceUsageChart
              resource="sink"
              title="Daily Write Trends"
              viewType={viewType}
              start={dates.startDate}
              end={dates.endDate}
              onDatesChanged={onDatesChanged}
            />
          </div>
        </Section>

        <Section id="resourceCount" title="" className={styles.section_borderless}>
          <ResourceCount
            detailed
            showShortTitle
            omitHeaderTextInfo
            controlled
            externalIsOrgView={isOrgView}
            externalOnControlChange={onControlChange}
          />
        </Section>
      </Stack>
      <Stack flexBasis="12px" />
    </Stack>
  );
};

Utilization.screenConfig = {
  path: `${ROUTE_REDESIGN}/monitor/utilization`,
  domain: 'monitor',
  header: {
    title: 'Utilization',
    slugTitle: 'Utilization',
  },
  screenName: 'Utilization',
};
